import { useEffect, useRef, useState } from 'react'
import type { Channel } from '../types'
import { ApiError, fetchChannel, parseSlug } from '../api/kick'
import { kickCache } from '../lib/cache'
import { useAppState } from '../state/appState'
import { formatViews } from '../lib/format'
import { Avatar } from './Avatar'

type Props = {
  onClose: () => void
  onAdded: (slug: string) => void
}

export function AddChannelPopover({ onClose, onAdded }: Props) {
  const { hasChannel, addTab } = useAppState()
  const [input, setInput] = useState('')
  const [channel, setChannel] = useState<Channel | null>(null)
  const [status, setStatus] = useState<string>('')
  const [checking, setChecking] = useState(false)
  const [favorite, setFavorite] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Debounced validation. Each keystroke supersedes the previous request.
  useEffect(() => {
    const slug = parseSlug(input)
    if (!slug) {
      setChannel(null)
      setStatus('')
      setChecking(false)
      return
    }

    const controller = new AbortController()
    setChecking(true)

    const timer = setTimeout(() => {
      kickCache
        .fetch(`channel:${slug}`, 60_000, (signal) => fetchChannel(slug, signal))
        .then((found) => {
          if (controller.signal.aborted) return
          setChannel(found)
          setChecking(false)
          if (hasChannel(found.slug)) {
            setStatus('Already open as a tab.')
          } else if (found.isBanned) {
            setStatus('This channel is banned on Kick. It may have no VODs.')
          } else if (!found.vodEnabled) {
            setStatus('This channel has VODs disabled, so the grid will stay empty.')
          } else {
            setStatus('')
          }
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return
          setChannel(null)
          setChecking(false)
          if (err instanceof ApiError && err.status === 404) {
            setStatus('No such Kick channel. Check the username.')
          } else {
            setStatus(err instanceof ApiError ? err.message : 'Lookup failed.')
          }
        })
    }, 400)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [input, hasChannel])

  const alreadyOpen = channel ? hasChannel(channel.slug) : false
  const canAdd = channel !== null && !alreadyOpen

  const submit = () => {
    if (!channel) return
    const slug = channel.slug
    // `addTab` returns null when the channel is already registered.
    if (!addTab(slug, favorite)) onAdded(slug)
    onAdded(slug)
  }

  const blocked = status.startsWith('No such') || status.startsWith('Lookup failed')

  return (
    <>
      <div className="popover-backdrop" onClick={onClose} />
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-label="Add a Kick channel"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
          if (e.key === 'Enter' && canAdd) submit()
        }}
      >
        <h2>Add a channel</h2>
        <p className="popover-hint">
          Enter a Kick username or channel URL — e.g. <code>xqc</code> or{' '}
          <code>kick.com/xqc</code>.
        </p>

        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="username or kick.com/…"
          spellCheck={false}
          autoComplete="off"
        />

        <div className={`popover-status ${blocked ? 'is-error' : ''}`}>
          {checking && !channel ? 'Checking channel…' : status}
        </div>

        {channel && (
          <div className="channel-preview">
            <Avatar src={channel.profilePic} name={channel.username} size={44} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="channel-preview-name">{channel.username}</div>
              <div className="channel-preview-meta">
                @{channel.slug} · {formatViews(channel.followers)} followers
                {channel.verified ? ' · Verified' : ''}
              </div>
              {channel.isLive && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  <span className="live-pill">
                    <span className="live-dot" /> Live
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--text-faint)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {channel.liveTitle}
                  </span>
                </div>
              )}
              {!alreadyOpen && (
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    marginTop: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={favorite}
                    onChange={(e) => setFavorite(e.target.checked)}
                    style={{ padding: 0, width: 'auto' }}
                  />
                  Favorite (pins this tab first)
                </label>
              )}
            </div>
          </div>
        )}

        <div className="popover-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!channel}>
            {alreadyOpen ? 'Go to tab' : 'Add channel'}
          </button>
        </div>
      </div>
    </>
  )
}